import { useState, type ReactNode } from 'react';
import {
  EXEMPLOS_PADRAO,
  LIMITES_TEMPLATE,
  extrairVariaveis,
  preencher,
  validarTemplate,
  type BotaoTemplate,
} from '@mpa/shared/template-text';
import { Campo, Form, LinhaCampos, ModalRodape } from './ui';
import { IDIOMAS } from '../dados/idiomas';

export interface TextoTemplate {
  header?: string | null;
  body: string;
  footer?: string | null;
  botoes?: BotaoTemplate[] | null;
  variaveis?: { nome: string; exemplo: string | null }[];
}

export interface ValoresTemplate {
  internalName: string;
  category: string;
  locale: string;
  purpose: string;
  header: string;
  body: string;
  footer: string;
  botoes: BotaoTemplate[];
}

const CATEGORIAS = [
  { valor: 'convite_jornada', rotulo: 'Convite para a jornada' },
  { valor: 'lembrete', rotulo: 'Lembrete' },
  { valor: 'recomendacao', rotulo: 'Recomendação de conexão' },
  { valor: 'reabertura', rotulo: 'Reabertura de conversa' },
  { valor: 'pos_evento', rotulo: 'Pós-evento' },
];

/**
 * Editor de texto de template, com pré-visualização.
 *
 * As mesmas regras da Meta rodam aqui e no servidor — `validarTemplate` vem de
 * `@mpa/shared`, não é reescrita. O objetivo é que o erro apareça enquanto se
 * digita, e não depois de dois dias de análise: a Meta responde "INVALID_FORMAT"
 * sem dizer onde.
 */
export function TemplateEditor({
  inicial,
  camposExtras,
  aoSalvar,
  aoCancelar,
  salvando,
  erro,
  rotuloSalvar = 'Salvar',
}: {
  inicial?: Partial<ValoresTemplate>;
  /** Campos que só uma das telas tem (a conta de WhatsApp, por exemplo). */
  camposExtras?: ReactNode;
  /**
   * `unknown` e não `string`: os botões são uma lista, e `FormData` só sabe
   * devolver texto. Eles entram no payload por fora do formulário, logo antes
   * de chamar quem salva.
   */
  aoSalvar: (dados: Record<string, unknown>) => void;
  aoCancelar: () => void;
  salvando?: boolean;
  erro?: unknown;
  rotuloSalvar?: string;
}) {
  const [body, setBody] = useState(inicial?.body ?? '');
  const [header, setHeader] = useState(inicial?.header ?? '');
  const [footer, setFooter] = useState(inicial?.footer ?? '');
  /**
   * Só o texto: todo botão daqui é de resposta rápida.
   *
   * A Meta também aceita botão de URL e de telefone, e o schema do servidor
   * continua aceitando os três. Mas nada nesta plataforma precisa deles — o que
   * se pergunta a um participante é "aceita ou não" —, e cada tipo a mais traz
   * um campo condicional e uma regra de validação própria.
   */
  const [botoes, setBotoes] = useState<string[]>(
    (inicial?.botoes ?? []).map((b) => b.texto),
  );

  const comoBotoes: BotaoTemplate[] = botoes
    .map((texto) => texto.trim())
    .filter(Boolean)
    .map((texto) => ({ tipo: 'RESPOSTA_RAPIDA', texto }));

  const problemas = validarTemplate({ header, body, footer, botoes: comoBotoes });
  const variaveis = extrairVariaveis(body);

  const trocarBotao = (i: number, texto: string) =>
    setBotoes((atuais) => atuais.map((b, j) => (j === i ? texto : b)));

  return (
    // Os botões viajam por fora do `FormData`, que só carrega texto. Vão sempre,
    // inclusive vazios: um PATCH sem `botoes` faz o servidor gravar `null` e
    // apagar os que já existiam — silenciosamente, ao editar qualquer vírgula.
    <Form aoEnviar={(dados) => aoSalvar({ ...dados, botoes: comoBotoes })}>
      {erro !== undefined && erro !== null && (
        <div className="form-erro">{(erro as Error).message}</div>
      )}

      <Campo
        label="Nome interno"
        name="internalName"
        obrigatorio
        autoFocus
        valorInicial={inicial?.internalName}
        placeholder="Convite para a jornada"
        ajuda="Só para a equipe. O nome na Meta é derivado deste."
      />

      <LinhaCampos>
        <Campo
          label="Tipo"
          name="category"
          opcoes={CATEGORIAS}
          valorInicial={inicial?.category ?? 'convite_jornada'}
        />
        <Campo
          label="Idioma"
          name="locale"
          opcoes={IDIOMAS}
          valorInicial={inicial?.locale ?? 'pt-BR'}
        />
      </LinhaCampos>

      {/* A categoria da Meta saiu daqui: tudo o que esta plataforma manda é
          divulgação, e vai como MARKETING. Era um seletor de três opções em que
          duas estavam erradas — e a errada mais tentadora, UTILITY, é mais
          barata e por isso escolhida com frequência. É a rejeição clássica de
          convite de networking, descoberta dias depois. O padrão agora está no
          schema do servidor. */}
      {camposExtras}

      <div className="divider" style={{ margin: '4px 0 16px' }} />

      <Campo
        label="Cabeçalho"
        name="header"
        valor={header}
        aoMudar={setHeader}
        placeholder="(opcional)"
        ajuda="Até 60 caracteres, no máximo uma variável."
      />

      <Campo
        label="Mensagem"
        name="body"
        obrigatorio
        linhas={6}
        valor={body}
        aoMudar={setBody}
        placeholder="Olá {{nome_participante}}, o {{nome_evento}} começa amanhã…"
        ajuda="Escreva variáveis com nome: {{nome_participante}}. A conversão para o formato da Meta é automática."
      />

      <Campo
        label="Rodapé"
        name="footer"
        valor={footer}
        aoMudar={setFooter}
        placeholder="(opcional)"
        ajuda="Até 60 caracteres, sem variáveis."
      />

      <div style={{ marginTop: 4 }}>
        <label className="form-label">Botões de resposta</label>
        <div className="form-ajuda" style={{ marginBottom: 8 }}>
          Até {LIMITES_TEMPLATE.botoes}. A pessoa toca em vez de digitar, e o WhatsApp entrega o
          texto do botão como se fosse a resposta dela.
        </div>

        <div style={{ display: 'grid', gap: 6 }}>
          {botoes.map((texto, i) => (
            <div key={i} style={{ display: 'flex', gap: 6 }}>
              <input
                className="form-input"
                value={texto}
                maxLength={LIMITES_TEMPLATE.textoBotao}
                placeholder="Vamos começar"
                onChange={(e) => trocarBotao(i, e.target.value)}
              />
              <button
                type="button"
                className="icon-btn"
                title="Remover botão"
                onClick={() => setBotoes((atuais) => atuais.filter((_, j) => j !== i))}
              >
                <i className="ti ti-trash" style={{ fontSize: 15 }} />
              </button>
            </div>
          ))}
        </div>

        {botoes.length < LIMITES_TEMPLATE.botoes && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            style={{ marginTop: botoes.length ? 8 : 0 }}
            onClick={() => setBotoes((atuais) => [...atuais, ''])}
          >
            <i className="ti ti-plus" /> Adicionar botão
          </button>
        )}
      </div>

      {problemas.length > 0 && (
        <div className="form-erro" style={{ display: 'grid', gap: 4 }}>
          {problemas.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </div>
      )}

      <PreVisualizacao header={header} body={body} footer={footer} botoes={comoBotoes} />

      {variaveis.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
          <strong>{variaveis.length}</strong> variável(is):{' '}
          {variaveis.map((v) => (
            <code key={v} className="mono" style={{ marginRight: 6 }}>
              {v}
            </code>
          ))}
        </div>
      )}

      <ModalRodape>
        <button type="button" className="btn btn-ghost" onClick={aoCancelar}>
          Cancelar
        </button>
        <button
          className="btn btn-primary"
          type="submit"
          disabled={salvando || problemas.length > 0}
        >
          {salvando ? 'Salvando…' : rotuloSalvar}
        </button>
      </ModalRodape>
    </Form>
  );
}

/**
 * Como a mensagem chega no aparelho.
 *
 * Vale a tela que ocupa: o texto com `{{nome_participante}}` no meio é difícil
 * de ler, e é lendo o resultado que se percebe a frase truncada, a saudação
 * repetida ou o tom errado — antes de a Meta gastar dois dias analisando.
 */
export function PreVisualizacao({
  header,
  body,
  footer,
  botoes,
  valores,
  recolhivel,
  abertaInicialmente = true,
}: {
  header?: string | null;
  body: string;
  footer?: string | null;
  botoes?: BotaoTemplate[] | null;
  valores?: Record<string, string>;
  /**
   * Deixa a prévia atrás de um resumo clicável.
   *
   * Usado em lista: com oito modelos abertos de uma vez, a página vira uma
   * parede de texto e escolher entre eles exige rolar por todos. No editor
   * fica sempre aberta — lá a prévia É o trabalho.
   */
  recolhivel?: boolean;
  abertaInicialmente?: boolean;
}) {
  /**
   * Estado próprio, mesmo com `<details>` sendo nativo.
   *
   * Passar `open` como atributo e deixar o navegador cuidar do resto parece
   * funcionar, até um re-render do React reimpor o valor do JSX e fechar o que
   * a pessoa acabou de abrir. Controlado, os dois concordam sempre.
   */
  const [aberta, setAberta] = useState(abertaInicialmente);

  // Os mesmos exemplos que o servidor manda para a Meta — se fossem duas
  // listas, aprovaria-se na tela um texto e mandaria-se outro.
  const exemplos = valores ?? EXEMPLOS_PADRAO;

  const previa = (
    <div className="previa-whatsapp">
      <div className="previa-balao">
        {header?.trim() && (
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{preencher(header, exemplos)}</div>
        )}
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {preencher(body, exemplos) || <span className="text-dim">A mensagem aparece aqui.</span>}
        </div>
        {footer?.trim() && (
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>
            {preencher(footer, exemplos)}
          </div>
        )}

        {/* Fora do balão no WhatsApp de verdade, colados embaixo dele. Ver os
            botões desenhados é o que mostra que "Não quero participar" não cabe
            em duas linhas antes de a Meta analisar. */}
        {botoes && botoes.length > 0 && (
          <div className="previa-botoes">
            {botoes.map((b, i) => (
              <div key={i} className="previa-botao">
                {b.texto}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (!recolhivel) return previa;

  return (
    <details
      className="previa-recolhivel"
      open={aberta}
      onToggle={(e) => setAberta(e.currentTarget.open)}
    >
      {/*
        O contador não é enfeite: o corpo de um template cabe em 1024
        caracteres, e o texto do consentimento chegou aqui com 1262. Ver o
        número sem abrir a mensagem é o que avisa antes da análise da Meta.
      */}
      <summary>
        <i className="ti ti-chevron-right seta" />
        <span>{aberta ? 'Esconder a mensagem' : 'Ver a mensagem'}</span>
        <span className="contador">{body.trim().length} caracteres</span>
      </summary>
      {previa}
    </details>
  );
}
